if (element.sectorToggle ===
<template>
  <div class="min-h-screen bg-primary w-full h-full themeTransition">
    <div v-if="!isLoading" class="sectionContainer place-content-start pt-[100px]">
      <page-wrapper v-if="pageGUI" :page-gui="pageGUI">
        <!-- content begin -->
        <page-header
          :title="properties?.title"
          :description="properties?.description.markup"
          :isUmbraco="isUmbraco"
          :theme-type="themeType"
        >
        </page-header>
        <div
          class="relative mb-10 col-start-1 col-span-full flex overflow-x-auto no-scrollbar 2xl:row-1 2xl:col-span-10 2xl:mb-8 2xl:overflow-x-visible"
        >
          <filter-tag
            v-for="tag in tags"
            :label="tag.title"
            :sectorToggle="isTagHidden(tag)"
            :selected="tag.id === activeFilter"
            :theme="themeType"
            classes="mr-3 shrink-0 first:ml-5 md:first:ml-8 md:last:mr-8 lg:first:ml-12 lg:last:mr-[108px] 2xl:first:ml-20"
            @handleClick="activeFilter = tag.id"
          />
        </div>
        <div class="col-start-2 col-span-12 mb-20 lg:mb-[140px]">
          <insights-grid :insights="insightsCards" />
        </div>
        <div
          v-if="showLoadMore"
          class="col-start-6 col-span-4 md:col-span-2 md:col-start-7 mb-36 mt-10"
        >
          <custom-button
            label="Load more"
            theme="light"
            type="secondary"
            @handleClick="handleLoadMore"
          />
        </div>
        <!-- content end -->
      </page-wrapper>
    </div>
  </div>
</template>

<script setup lang="ts">
import { IInsightsOverviewPage } from "@/types/IInsightsOverviewPage";
import {
  getPage,
  getPageByID,
  getFilterTags,
  getInsights,
  getTranslations,
  getAllInsights,
} from "@/util/api";
import { getThemeType, changeTheme, formatDate } from "@/util/helper";
import { computed, ref, onBeforeMount, watch } from "vue";
import PageWrapper from "@components/PageWrapper.vue";
import PageHeader from "@atoms/PageHeader/PageHeader.vue";
import CustomButton from "@atoms/Button/CustomButton.ce.vue";
import { FilterTagType } from "@/util/constants";
import { FilterType, IFilterTag, Theme } from "@/types/types";
import { IInsightPage } from "@/types/IInsightPage";
import InsightsGrid, { IInsight } from "@organisms/InsightsGrid/InsightsGrid.vue";
import FilterTag from "@components/Atoms/FilterTag/FilterTag.ce.vue";
import { getPathForPageData } from "@/util/getPathForPageData";

const isLoading = ref(true);
const pageData = ref<IInsightsOverviewPage | null>(null);
const theme = ref<Theme | null>();
const pageGUI = ref<string | undefined>();
const pageId = ref<string | undefined>();
const insightsCards = ref<IInsight[]>([]);
const tags = ref<IFilterTag[] | null>(null);
const seeAllTagName = ref<string>("");
const activeFilter = ref("");
const pageNumber = ref(1);
const pageSize = ref(8);
const pageKey = ref<string | undefined>();
const currentPageLabel = ref("hideFromInsights");

const isUmbraco = ref(false);

const fetchInitialData = async () => {
  pageKey.value = document.documentElement.dataset.id;
  const pageParams = getPathForPageData();
  if (pageParams?.path && pageKey.value) {
    [pageData.value, seeAllTagName.value, tags.value] = await Promise.all([
      getPageByID(pageKey.value, pageParams?.isPreview) as Promise<IInsightsOverviewPage>,
      //getPage(pageParams?.path, pageParams?.isPreview) as Promise<IInsightsOverviewPage>,
      getTranslations("SeeAllInsights"),
      getFilterTags(FilterTagType.SECTOR_TAG),
    ]);

    tags.value = [
      {
        id: "all",
        title: seeAllTagName.value,
        contentType: "sectorTag" as FilterType,
      },
      ...tags.value,
    ];
    activeFilter.value = tags.value[0].id;

    await handlePageData();
  }
};

const handlePageData = async () => {
  if (!pageData.value) return;
  pageGUI.value = pageData.value.route.startItem.id;
  pageId.value = document.documentElement.dataset.id;
  updateTheme();
  const filteredInsight = await fetchInsights();
  if (!filteredInsight) return;
  insightsCards.value = createInsightsCards(filteredInsight);
  isLoading.value = false;
};

const updateTheme = () => {
  if (properties.value) {
    const backgroundColor = properties.value.backgroundColor;
    theme.value = backgroundColor ? backgroundColor : theme.value;
  }
  if (theme.value) changeTheme(theme.value);
};

onBeforeMount(fetchInitialData);

watch(activeFilter, (value) => {
  if (value) {
    pageNumber.value = 1;
    fetchInsights().then((insights) => {
      if (!insights) return;
      insightsCards.value = createInsightsCards(insights);
    });
  }
});

const properties = computed(() => {
  return pageData.value?.properties;
});

const showLoadMore = computed(() => {
  return insightsCards.value.length === pageSize.value;
});

const themeType = computed(() => {
  if (!theme.value) return "dark";
  return getThemeType(theme.value);
});

async function fetchInsights() {
  if (!pageId?.value) return;
  let insights: IInsightPage[] = [];
  if (activeFilter.value === "all") {
    insights = await getAllInsights(pageNumber.value, pageSize.value);
  } else {
    insights = await getInsights(
      pageId.value,
      activeFilter.value,
      pageNumber.value,
      pageSize.value
    );
  }
  return insights;
}

function isTagHidden(tag: IFilterTag) {
  return tag.sectorToggle?.includes("hideFromInsights") ? false : true;
}

async function handleLoadMore() {
  pageNumber.value++;
  const addedInsights = await fetchInsights();
  if (!addedInsights) return;
  const addedInsightsCards = createInsightsCards(addedInsights);
  insightsCards.value = [...insightsCards.value, ...addedInsightsCards];
}

function createInsightsCards(insights: IInsightPage[]): IInsight[] {
  const insightsCards = insights.map((n: IInsightPage) => {
    const date = n.properties.date ? formatDate(n.properties.date) : null;
    return {
      image: n.properties.image && JSON.stringify(n.properties.image[0]),
      imgAlt: n?.properties?.imageAltText,
      date: date,
      title: n.name,
      href: n.route.path,
      theme: theme?.value,
      tag: n?.properties?.projectSector[0]?.name,
    };
  });
  return insightsCards;
}
</script>
```
